import React, { useState } from 'react';
import Avatar from '@atlaskit/avatar';
import { IconButton } from '@atlaskit/button/new';
import EditIcon from '@atlaskit/icon/core/migration/edit--edit-filled';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import { ProjectIconSmallerSize } from '@atlassian/jira-horizontal-nav-icons/src/ui/icon/index.tsx';
import messages from './messages.tsx';
import type { EditableAvatarProps } from './types.tsx';

const EditIconWithTransparentBackground = () => (
	<Box xcss={iconBackgroundStyles}>
		<EditIcon label="" color={token('color.icon.inverse')} />
	</Box>
);

const ProjectAvatar = ({ src, testId }: { src: string; testId: string }) =>
	fg('blu-6305_tab_item_styling_in_horizontal_navigation') ? (
		<ProjectIconSmallerSize
			data-testid={testId && `${testId}--image`}
			data-vc="editable-avatar-project"
			src={src}
			alt=""
		/>
	) : (
		<Avatar
			appearance="square"
			borderColor="transparent"
			size="small"
			src={src}
			testId={testId}
			data-vc="editable-avatar-project"
		/>
	);

export const EditableAvatar = (props: EditableAvatarProps) => {
	const { avatarUrl, canEdit, triggerProps = {}, onClick } = props;
	const [isHover, setIsHover] = useState(false);
	const { formatMessage } = useIntl();

	return (
		// eslint-disable-next-line jsx-a11y/no-static-element-interactions
		<span onMouseEnter={() => setIsHover(true)} onMouseLeave={() => setIsHover(false)}>
			{canEdit ? (
				<Tooltip content={formatMessage(messages.editIconLabel)} position="bottom">
					{(tooltipProps) => (
						<Box xcss={editableAvatarContainerStyles}>
							{isHover && (
								<Box
									xcss={
										fg('blu-6305_tab_item_styling_in_horizontal_navigation')
											? smallerIconButtonWrapperStyles
											: iconButtonWrapperStyles
									}
									// eslint-disable-next-line react/jsx-props-no-spreading
									{...tooltipProps}
								>
									<IconButton
										appearance="subtle"
										icon={EditIconWithTransparentBackground}
										label={formatMessage(messages.editIconLabel)}
										spacing="compact"
										{...triggerProps}
										onClick={onClick}
									/>
								</Box>
							)}
							<ProjectAvatar
								src={avatarUrl ? `${avatarUrl}?size=xlarge` : avatarUrl}
								testId="navigation-apps-sidebar-inline-config-project-header.ui.editable-avatar.project-icon-editable"
							/>
						</Box>
					)}
				</Tooltip>
			) : (
				<ProjectAvatar
					src={avatarUrl ? `${avatarUrl}?size=xlarge` : avatarUrl}
					testId="navigation-apps-sidebar-inline-config-project-header.ui.editable-avatar.project-icon"
				/>
			)}
		</span>
	);
};

const iconBackgroundStyles = xcss({
	backgroundColor: 'color.background.neutral.pressed',
	borderRadius: '2px',
});

const editableAvatarContainerStyles = xcss({ position: 'relative' });

const iconButtonWrapperStyles = xcss({
	position: 'absolute',
	zIndex: 'card',
	left: token('space.025'),
	top: token('space.025'),
});

const smallerIconButtonWrapperStyles = xcss({
	position: 'absolute',
	zIndex: 'card',
	left: token('space.negative.025'),
	top: token('space.negative.025'),
});
